import React, { useEffect, useState } from "react";
import { WifiOff, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "@/lib/toast";
import { useOfflineStatus } from "../hooks/useOnlineSync";

export default function OfflineBanner() {
  const { isOnline, outboxStatus, isSyncing, retryFailed } = useOfflineStatus();
  const { total = 0, failed = 0, pending = 0 } = outboxStatus || {};

  // Delay showing offline state 2 s to avoid flicker on load or brief dropouts.
  // Failed/syncing states bypass this delay — they're always shown immediately.
  const [offlineReady, setOfflineReady] = useState(false);
  useEffect(() => {
    if (isOnline) { setOfflineReady(false); return; }
    const t = setTimeout(() => setOfflineReady(true), 2000);
    return () => clearTimeout(t);
  }, [isOnline]);

  // Show a toast when the sync engine hits a 429 rate-limit response.
  useEffect(() => {
    const handle = () =>
      toast.warning("Demasiadas solicitudes. La sincronización se retomará en unos minutos.", {
        id: "rate-limit",
        duration: 6000,
      });
    window.addEventListener("keves:rate-limit", handle);
    return () => window.removeEventListener("keves:rate-limit", handle);
  }, []);

  // Priority 1 — failed items need attention
  if (failed > 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/40 text-sm text-red-700 dark:text-red-400">
        <AlertCircle size={13} className="shrink-0" />
        <span className="flex-1">
          {failed} cambio{failed > 1 ? 's' : ''} no se {failed > 1 ? 'pudieron' : 'pudo'} sincronizar
        </span>
        <button
          onClick={retryFailed}
          disabled={isSyncing}
          className="font-semibold underline underline-offset-2 disabled:opacity-40 shrink-0 p-2 -m-2"
        >
          {isSyncing ? "Reintentando…" : "Reintentar"}
        </button>
      </div>
    );
  }

  // Priority 2 — actively syncing
  if (isSyncing) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800/40 text-sm text-blue-600 dark:text-blue-400">
        <RefreshCw size={13} className="shrink-0 animate-spin" />
        <span>
          Sincronizando{pending > 0 ? ` ${pending} cambio${pending > 1 ? 's' : ''}` : ''}…
        </span>
      </div>
    );
  }

  // Priority 3 — offline (delayed 2 s to avoid load-time flicker)
  if (!isOnline && offlineReady) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/40 text-sm text-amber-700 dark:text-amber-400">
        <WifiOff size={13} className="shrink-0" />
        <span>
          Sin conexión
          {total > 0 && (
            <span className="ml-1 opacity-75">
              · {total} cambio{total > 1 ? 's' : ''} pendiente{total > 1 ? 's' : ''}
            </span>
          )}
        </span>
      </div>
    );
  }

  // Priority 4 — online but pending items (about to sync automatically — transient, non-urgent)
  if (pending > 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-700/40 text-[13px] text-slate-500 dark:text-slate-400">
        <RefreshCw size={13} className="shrink-0 opacity-60" />
        <span>{pending} {pending === 1 ? 'cambio guardado' : 'cambios guardados'} · sincronizando pronto</span>
      </div>
    );
  }

  return null;
}
